import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditConnectionDialog } from '@/features/projects/project-details/components/edit-connection-dialog';
import {
  ProjectPropertiesMenuItem,
  ProjectPropertiesMenuItemPrefix,
  ProjectPropertiesMenuItemValue,
} from '@/features/projects/project-details/components/layouts/project-properties-menu-layout';
import { useUpdateProjectConnection } from '@/features/projects/project-details/hooks/use-update-project-connection';
import { useProjects } from '@/features/projects/shared/hooks/use-projects';
import { toast } from '@/shared/hooks/use-toast';

interface ConnectionItemProps {
  projectId: number;
  connectedProjectId?: number | null;
}

export const ConnectionItem = ({
  projectId,
  connectedProjectId,
}: ConnectionItemProps) => {
  const { t } = useTranslation('project', { keyPrefix: 'ProjectConnection' });
  const queryClient = useQueryClient();

  // for optimistic update
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    connectedProjectId ?? null,
  );
  const { isLoading, data: projects } = useProjects();
  const mutation = useUpdateProjectConnection({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      toast({ title: t('update_connection_success') });
    },
    onError: () => {
      setSelectedProjectId(connectedProjectId ?? null);
      toast({ title: t('update_connection_error') });
    },
  });

  const handleConnectionChange = (newConnectedProjectId: number | null) => {
    setSelectedProjectId(newConnectedProjectId);
    mutation.mutate({
      projectId,
      connectedProjectId: newConnectedProjectId,
    });
  };

  const connectedProject = selectedProjectId
    ? projects?.find((p) => p.id === selectedProjectId)
    : null;

  const [isOpen, setIsOpen] = useState(false);

  return (
    <EditConnectionDialog
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      projectId={projectId}
      connectedProjectId={selectedProjectId}
      projects={projects}
      isLoading={isLoading}
      onSelect={handleConnectionChange}
    >
      <div>
        <ProjectPropertiesMenuItem isEditable onEdit={() => setIsOpen(true)}>
          <ProjectPropertiesMenuItemPrefix>
            {t('connection')}
          </ProjectPropertiesMenuItemPrefix>
          <ProjectPropertiesMenuItemValue>
            <span>{connectedProject?.name || t('no_connection')}</span>
          </ProjectPropertiesMenuItemValue>
        </ProjectPropertiesMenuItem>
      </div>
    </EditConnectionDialog>
  );
};
